import { useState, useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    TextField,
    Checkbox,
    Radio,
    RadioGroup,
    CircularProgress,
    Rating
} from "@mui/material";
import {
    Close as CloseIcon,
    ArrowForward as BackIcon,
    Save as SaveIcon,
    AttachFile as AttachFileIcon
} from "@mui/icons-material";
import { useMessageContext } from "../../contexts/MessageContext";
import { getFormById, saveFormAnswers } from "../../services/FormTemplateService";
import { uploadImage, uploadPdf, uploadMedia } from "../../services/MediaService";
import { t as coachT } from "../dashboard/coach/coachTokens";

// Question type constants (matching server types)
const QUESTION_TYPES = {
    FORM_TITLE: 'FORM_TITLE',
    SECTION_TITLE: 'SECTION_TITLE',
    YES_NO: 'YES_NO',
    SHORT_TEXT: 'SHORT_TEXT',
    LONG_TEXT: 'LONG_TEXT',
    MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
    LINK: 'LINK',
    NUMBER: 'NUMBER',
    DATE: 'DATE',
    FILE_UPLOAD: 'FILE_UPLOAD',
    PDF_ATTACHMENT: 'PDF_ATTACHMENT',
    SIGNATURE: 'SIGNATURE',
    CHECKBOX: 'CHECKBOX',
    FREE_TEXT: 'FREE_TEXT',
    EMAIL: 'EMAIL',
    PHONE: 'PHONE',
    RATING: 'RATING'
};

// Validation helpers
const validateEmail = (email) => {
    if (!email) return true; // Empty is valid (required check is separate)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const validatePhone = (phone) => {
    if (!phone) return true; // Empty is valid (required check is separate)
    const phoneRegex = /^[\d\-+() ]{7,20}$/;
    return phoneRegex.test(phone);
};

const FormEditDialog = ({ open, onClose, onSave, form, clientName, coach = false }) => {
    const { notificationHandler } = useMessageContext();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState({});
    const signatureRefs = useRef({});
    const fileInputRefs = useRef({});

    // Load form questions when dialog opens
    useEffect(() => {
        if (open && form) {
            loadFormData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, form?.id]);

    const loadFormData = async () => {
        setLoading(true);
        try {
            // Fetch form with questions and existing answers from server
            const response = await getFormById(form.id);
            const formData = response.data;

            // Sort questions by orderIndex
            const sortedQuestions = (formData.questions || [])
                .sort((a, b) => a.orderIndex - b.orderIndex);

            setQuestions(sortedQuestions);

            // Initialize answers from existing data
            const initialAnswers = {};
            const existingAnswers = formData.answers || [];

            sortedQuestions.forEach(q => {
                // Find existing answer for this question
                const existingAnswer = existingAnswers.find(a => a.questionId === q.id);

                if (existingAnswer) {
                    // Map existing answer based on type
                    initialAnswers[q.id] = mapExistingAnswer(existingAnswer, q.questionType);
                } else {
                    // Initialize empty answer - for titles, use the question's title as default
                    if (q.questionType === QUESTION_TYPES.FORM_TITLE || q.questionType === QUESTION_TYPES.SECTION_TITLE) {
                        initialAnswers[q.id] = { answer: q.title || q.question || "", details: "" };
                    } else {
                        initialAnswers[q.id] = getEmptyAnswer(q.questionType);
                    }
                }
            });
            setAnswers(initialAnswers);
        } catch (error) {
            console.error('Error loading form data:', error);
            notificationHandler.error("שגיאה בטעינת הטופס");
        } finally {
            setLoading(false);
        }
    };

    // Map existing answer from server to local format
    const mapExistingAnswer = (answer, questionType) => {
        switch (questionType) {
            case QUESTION_TYPES.FORM_TITLE:
            case QUESTION_TYPES.SECTION_TITLE:
            case QUESTION_TYPES.SHORT_TEXT:
            case QUESTION_TYPES.LONG_TEXT:
            case QUESTION_TYPES.EMAIL:
            case QUESTION_TYPES.PHONE:
                return { answer: answer.text || "", details: "" };
            case QUESTION_TYPES.YES_NO:
                return { answer: answer.answer, details: answer.details || "", detailsNo: answer.detailsNo || "" };
            case QUESTION_TYPES.NUMBER:
                return { answer: answer.number ?? "", details: "" };
            case QUESTION_TYPES.DATE:
                // Handle date as array [year, month, day] from server
                if (Array.isArray(answer.date)) {
                    const [year, month, day] = answer.date;
                    return { answer: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, details: "" };
                }
                return { answer: answer.date || "", details: "" };
            case QUESTION_TYPES.MULTIPLE_CHOICE:
                return { answer: answer.selectedOptions || [], details: "" };
            case QUESTION_TYPES.RATING:
                return { answer: answer.rating || 0, details: "" };
            case QUESTION_TYPES.CHECKBOX:
                return { answer: answer.checked || false, details: "" };
            case QUESTION_TYPES.FILE_UPLOAD:
                // Server returns uploadedFileUrls as array
                const fileUrls = answer.uploadedFileUrls || [];
                return { answer: fileUrls.length > 0 ? fileUrls[0] : (answer.fileUrl || answer.url || ""), details: "" };
            case QUESTION_TYPES.SIGNATURE:
                // Server uses signatureImageUrl
                return { answer: answer.signatureImageUrl || answer.signatureUrl || answer.url || "", details: "" };
            default:
                return { answer: null, details: "" };
        }
    };

    // Get empty answer based on question type
    const getEmptyAnswer = (questionType) => {
        switch (questionType) {
            case QUESTION_TYPES.MULTIPLE_CHOICE:
                return { answer: [], details: "" };
            case QUESTION_TYPES.RATING:
                return { answer: 0, details: "" };
            case QUESTION_TYPES.CHECKBOX:
                return { answer: false, details: "" };
            case QUESTION_TYPES.YES_NO:
                return { answer: null, details: "", detailsNo: "" };
            default:
                return { answer: "", details: "" };
        }
    };

    // Clear a question's validation error once the user starts answering it (so a stale
    // "שדה חובה" doesn't linger after the field is filled). EMAIL/PHONE inputs clear inline too.
    const clearValidationError = (questionId) => {
        setValidationErrors(prev => {
            if (!prev[questionId]) return prev;
            const next = { ...prev };
            delete next[questionId];
            return next;
        });
    };

    const handleAnswerChange = (questionId, value, field = 'answer') => {
        clearValidationError(questionId);
        setAnswers(prev => ({
            ...prev,
            [questionId]: {
                ...prev[questionId],
                [field]: value
            }
        }));
    };

    const handleMultipleChoiceChange = (questionId, option, checked) => {
        clearValidationError(questionId);
        setAnswers(prev => {
            const currentAnswers = prev[questionId]?.answer || [];
            let newAnswers;
            if (checked) {
                newAnswers = [...currentAnswers, option];
            } else {
                newAnswers = currentAnswers.filter(a => a !== option);
            }
            return {
                ...prev,
                [questionId]: {
                    ...prev[questionId],
                    answer: newAnswers
                }
            };
        });
    };

    // Map answer type for API
    const getAnswerType = (questionType) => {
        switch (questionType) {
            case QUESTION_TYPES.FORM_TITLE:
            case QUESTION_TYPES.SECTION_TITLE:
            case QUESTION_TYPES.SHORT_TEXT:
            case QUESTION_TYPES.LONG_TEXT:
            case QUESTION_TYPES.EMAIL:
            case QUESTION_TYPES.PHONE:
                return 'TEXT';
            case QUESTION_TYPES.YES_NO:
                return 'YES_NO';
            case QUESTION_TYPES.NUMBER:
                return 'NUMBER';
            case QUESTION_TYPES.DATE:
                return 'DATE';
            case QUESTION_TYPES.MULTIPLE_CHOICE:
                return 'MULTIPLE_CHOICE';
            case QUESTION_TYPES.RATING:
                return 'RATING';
            case QUESTION_TYPES.CHECKBOX:
                return 'CHECKBOX';
            case QUESTION_TYPES.FILE_UPLOAD:
                return 'FILE_UPLOAD';
            case QUESTION_TYPES.SIGNATURE:
                return 'SIGNATURE';
            default:
                return null;
        }
    };

    // Build answer object for API
    const buildAnswerForApi = (question, answerData) => {
        const answerType = getAnswerType(question.questionType);
        if (!answerType) return null;

        const baseAnswer = {
            questionId: question.id,
            answerType: answerType
        };

        switch (question.questionType) {
            case QUESTION_TYPES.FORM_TITLE:
            case QUESTION_TYPES.SECTION_TITLE:
            case QUESTION_TYPES.SHORT_TEXT:
            case QUESTION_TYPES.LONG_TEXT:
            case QUESTION_TYPES.EMAIL:
            case QUESTION_TYPES.PHONE:
                return { ...baseAnswer, text: answerData.answer || "" };
            case QUESTION_TYPES.YES_NO:
                return { ...baseAnswer, answer: answerData.answer, details: answerData.details || "", detailsNo: answerData.detailsNo || "" };
            case QUESTION_TYPES.NUMBER:
                return { ...baseAnswer, number: answerData.answer !== "" ? Number(answerData.answer) : null };
            case QUESTION_TYPES.DATE:
                return { ...baseAnswer, date: answerData.answer || null };
            case QUESTION_TYPES.MULTIPLE_CHOICE:
                // Ensure selectedOptions is always an array
                const selectedOptions = Array.isArray(answerData.answer)
                    ? answerData.answer
                    : (answerData.answer ? [answerData.answer] : []);
                return { ...baseAnswer, selectedOptions };
            case QUESTION_TYPES.RATING:
                return { ...baseAnswer, rating: answerData.answer || 0 };
            case QUESTION_TYPES.CHECKBOX:
                return { ...baseAnswer, checked: answerData.answer || false };
            case QUESTION_TYPES.FILE_UPLOAD:
                // Server expects uploadedFileUrls as array
                return { ...baseAnswer, uploadedFileUrls: answerData.answer ? [answerData.answer] : [] };
            case QUESTION_TYPES.SIGNATURE:
                // Server expects signatureImageUrl
                return { ...baseAnswer, signatureImageUrl: answerData.answer || null };
            default:
                return null;
        }
    };

    // Display-only question types carry no answer, so they are never "required".
    const DISPLAY_ONLY_TYPES = [
        QUESTION_TYPES.FORM_TITLE,
        QUESTION_TYPES.SECTION_TITLE,
        QUESTION_TYPES.FREE_TEXT,
        QUESTION_TYPES.LINK,
        QUESTION_TYPES.PDF_ATTACHMENT,
    ];

    // True when a required question has no meaningful answer. Answer shapes vary by type
    // (see buildAnswerForApi): most use answerData.answer, MULTIPLE_CHOICE uses an array,
    // YES_NO expects a yes/no value. Number 0 and boolean false are valid answers.
    const isAnswerEmpty = (question, answerData) => {
        const value = answerData?.answer;
        switch (question.questionType) {
            case QUESTION_TYPES.MULTIPLE_CHOICE: {
                const arr = Array.isArray(value) ? value : (value ? [value] : []);
                return arr.length === 0;
            }
            case QUESTION_TYPES.CHECKBOX:
                // A required checkbox must be checked (true).
                return value !== true;
            case QUESTION_TYPES.NUMBER:
            case QUESTION_TYPES.RATING:
                // 0 is a valid numeric answer; only null/undefined/'' are empty.
                return value === null || value === undefined || value === "";
            default:
                // Text, email, phone, date, yes/no, signature, file: empty string / nullish is empty.
                return value === null || value === undefined || value === "";
        }
    };

    const handleSave = async () => {
        // Check for validation errors before saving
        let hasErrors = false;
        const newValidationErrors = {};

        questions.forEach(question => {
            const answerData = answers[question.id];

            // Required-field enforcement: a question marked required must have an answer.
            // (Display-only types can't be required; they carry no answer.)
            if (question.required && !DISPLAY_ONLY_TYPES.includes(question.questionType)
                && isAnswerEmpty(question, answerData)) {
                newValidationErrors[question.id] = "שדה חובה";
                hasErrors = true;
                return; // don't also run format checks on an empty required field
            }

            if (question.questionType === QUESTION_TYPES.EMAIL && answerData?.answer) {
                if (!validateEmail(answerData.answer)) {
                    newValidationErrors[question.id] = "כתובת אימייל לא תקינה";
                    hasErrors = true;
                }
            }
            if (question.questionType === QUESTION_TYPES.PHONE && answerData?.answer) {
                if (!validatePhone(answerData.answer)) {
                    newValidationErrors[question.id] = "מספר טלפון לא תקין";
                    hasErrors = true;
                }
            }
        });

        if (hasErrors) {
            setValidationErrors(newValidationErrors);
            notificationHandler.error("יש לתקן שגיאות בטופס לפני השליחה");
            return;
        }

        setSaving(true);
        try {
            // Build answers array for API
            const answersForApi = [];
            questions.forEach(question => {
                // Skip display-only question types (they don't have answers)
                if (question.questionType === QUESTION_TYPES.FORM_TITLE ||
                    question.questionType === QUESTION_TYPES.SECTION_TITLE ||
                    question.questionType === QUESTION_TYPES.FREE_TEXT ||
                    question.questionType === QUESTION_TYPES.LINK ||
                    question.questionType === QUESTION_TYPES.PDF_ATTACHMENT) {
                    return;
                }

                const answerData = answers[question.id];
                if (answerData) {
                    const apiAnswer = buildAnswerForApi(question, answerData);
                    if (apiAnswer) {
                        answersForApi.push(apiAnswer);
                    }
                }
            });

            // Save answers via API
            await saveFormAnswers(form.id, answersForApi);

            notificationHandler.success("התשובות נשמרו בהצלחה");
            onSave();
        } catch (error) {
            console.error('Error saving answers:', error);
            notificationHandler.error("שגיאה בשמירת התשובות");
        } finally {
            setSaving(false);
        }
    };

    const renderQuestionInput = (question) => {
        const answer = answers[question.id] || { answer: null, details: "" };

        switch (question.questionType) {
            case QUESTION_TYPES.FORM_TITLE:
                return (
                    <Typography
                        sx={{
                            fontSize: "24px",
                            fontWeight: 600,
                            color: "#1E293B",
                            textAlign: "center",
                            padding: "16px 0",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {question.title || question.question}
                    </Typography>
                );

            case QUESTION_TYPES.SECTION_TITLE:
                return (
                    <Typography
                        sx={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: "#1E293B",
                            borderBottom: "2px solid #57837E",
                            paddingBottom: "8px",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {question.title || question.question}
                    </Typography>
                );

            case QUESTION_TYPES.FREE_TEXT:
                return (
                    <Box
                        sx={{
                            backgroundColor: "#E8F5F3",
                            borderRight: "4px solid #57837E",
                            padding: "16px",
                            borderRadius: "0 8px 8px 0"
                        }}
                    >
                        <Typography sx={{ fontSize: "14px", color: "#1E293B", whiteSpace: "pre-wrap" }}>
                            {question.content || question.question}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPES.SHORT_TEXT:
                return (
                    <TextField
                        fullWidth
                        value={answer.answer || ""}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={question.placeholder || "הזן תשובה..."}
                        type="text"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            },
                            "& .MuiOutlinedInput-input": {
                                textAlign: "right",
                                direction: "rtl",
                                "&::placeholder": {
                                    textAlign: "right",
                                    direction: "rtl"
                                }
                            }
                        }}
                    />
                );

            case QUESTION_TYPES.EMAIL:
                const emailError = answer.answer && !validateEmail(answer.answer);
                return (
                    <TextField
                        fullWidth
                        value={answer.answer || ""}
                        onChange={(e) => {
                            handleAnswerChange(question.id, e.target.value);
                            if (validationErrors[question.id]) {
                                setValidationErrors(prev => ({ ...prev, [question.id]: null }));
                            }
                        }}
                        onBlur={() => {
                            if (answer.answer && !validateEmail(answer.answer)) {
                                setValidationErrors(prev => ({ ...prev, [question.id]: "כתובת אימייל לא תקינה" }));
                            }
                        }}
                        placeholder={question.placeholder || "example@email.com"}
                        type="email"
                        error={emailError || !!validationErrors[question.id]}
                        helperText={validationErrors[question.id] || (emailError ? "כתובת אימייל לא תקינה" : "")}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            },
                            "& .MuiOutlinedInput-input": {
                                textAlign: "right",
                                direction: "rtl",
                                "&::placeholder": {
                                    textAlign: "right",
                                    direction: "rtl"
                                }
                            }
                        }}
                    />
                );

            case QUESTION_TYPES.PHONE:
                const phoneError = answer.answer && !validatePhone(answer.answer);
                return (
                    <TextField
                        fullWidth
                        value={answer.answer || ""}
                        onChange={(e) => {
                            handleAnswerChange(question.id, e.target.value);
                            if (validationErrors[question.id]) {
                                setValidationErrors(prev => ({ ...prev, [question.id]: null }));
                            }
                        }}
                        onBlur={() => {
                            if (answer.answer && !validatePhone(answer.answer)) {
                                setValidationErrors(prev => ({ ...prev, [question.id]: "מספר טלפון לא תקין" }));
                            }
                        }}
                        placeholder={question.placeholder || "050-0000000"}
                        type="tel"
                        error={phoneError || !!validationErrors[question.id]}
                        helperText={validationErrors[question.id] || (phoneError ? "מספר טלפון לא תקין" : "")}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            },
                            "& .MuiOutlinedInput-input": {
                                textAlign: "right",
                                direction: "rtl",
                                "&::placeholder": {
                                    textAlign: "right",
                                    direction: "rtl"
                                }
                            }
                        }}
                    />
                );

            case QUESTION_TYPES.LONG_TEXT:
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={answer.answer || ""}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={question.placeholder || "הזן תשובה מפורטת..."}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                    />
                );

            case QUESTION_TYPES.NUMBER:
                return (
                    <TextField
                        type="number"
                        value={answer.answer || ""}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder="0"
                        sx={{
                            width: "200px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                    />
                );

            case QUESTION_TYPES.DATE:
                return (
                    <TextField
                        type="date"
                        value={answer.answer || ""}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        sx={{
                            width: "200px",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                );

            case QUESTION_TYPES.YES_NO:
                return (
                    <Box>
                        <Box sx={{ display: "flex", gap: "12px", marginBottom: (question.showDetailsOnYes && answer.answer === true) || (question.showDetailsOnNo && answer.answer === false) ? "12px" : 0 }}>
                            <Button
                                variant={answer.answer === true ? "contained" : "outlined"}
                                onClick={() => handleAnswerChange(question.id, true)}
                                sx={{
                                    backgroundColor: answer.answer === true ? "#57837E" : "transparent",
                                    borderColor: answer.answer === true ? "#57837E" : "#E2E8F0",
                                    color: answer.answer === true ? "#FFFFFF" : "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    "&:hover": {
                                        backgroundColor: answer.answer === true ? "#46706B" : "#F8FAFC",
                                        borderColor: "#57837E"
                                    }
                                }}
                            >
                                כן
                            </Button>
                            <Button
                                variant={answer.answer === false ? "contained" : "outlined"}
                                onClick={() => handleAnswerChange(question.id, false)}
                                sx={{
                                    backgroundColor: answer.answer === false ? "#57837E" : "transparent",
                                    borderColor: answer.answer === false ? "#57837E" : "#E2E8F0",
                                    color: answer.answer === false ? "#FFFFFF" : "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    "&:hover": {
                                        backgroundColor: answer.answer === false ? "#46706B" : "#F8FAFC",
                                        borderColor: "#57837E"
                                    }
                                }}
                            >
                                לא
                            </Button>
                        </Box>
                        {question.showDetailsOnYes && answer.answer === true && (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={answer.details || ""}
                                onChange={(e) => handleAnswerChange(question.id, e.target.value, 'details')}
                                placeholder={question.detailsPlaceholder || "פרטים נוספים..."}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                        {question.showDetailsOnNo && answer.answer === false && (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                value={answer.detailsNo || ""}
                                onChange={(e) => handleAnswerChange(question.id, e.target.value, 'detailsNo')}
                                placeholder={question.detailsPlaceholderNo || "פרטים נוספים..."}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                    </Box>
                );

            case QUESTION_TYPES.MULTIPLE_CHOICE:
                if (question.allowMultiple) {
                    return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {question.options?.map((option, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "12px",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        backgroundColor: (answer.answer || []).includes(option) ? "#E8F5F3" : "transparent",
                                        "&:hover": {
                                            backgroundColor: "#F8FAFC"
                                        }
                                    }}
                                    onClick={() => handleMultipleChoiceChange(question.id, option, !(answer.answer || []).includes(option))}
                                >
                                    <Checkbox
                                        checked={(answer.answer || []).includes(option)}
                                        size="small"
                                        sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                                    />
                                    <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        {option}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    );
                } else {
                    return (
                        <RadioGroup
                            value={answer.answer || ""}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        >
                            {question.options?.map((option, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "12px",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        marginBottom: "8px",
                                        backgroundColor: answer.answer === option ? "#E8F5F3" : "transparent",
                                        "&:hover": {
                                            backgroundColor: "#F8FAFC"
                                        }
                                    }}
                                    onClick={() => handleAnswerChange(question.id, option)}
                                >
                                    <Radio
                                        value={option}
                                        size="small"
                                        sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                                    />
                                    <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        {option}
                                    </Typography>
                                </Box>
                            ))}
                        </RadioGroup>
                    );
                }

            case QUESTION_TYPES.RATING:
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
                            {Math.min(question.maxRating || 5, 5)} / {answer.answer || 0}
                        </Typography>
                        <Rating
                            value={answer.answer || 0}
                            onChange={(e, newValue) => handleAnswerChange(question.id, newValue)}
                            max={Math.min(question.maxRating || 5, 5)}
                            size="large"
                            sx={{
                                "& .MuiRating-iconFilled": {
                                    color: "#57837E"
                                },
                                "& .MuiRating-iconHover": {
                                    color: "#46706B"
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPES.CHECKBOX:
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "16px",
                            border: "2px solid #E2E8F0",
                            borderRadius: "8px",
                            cursor: "pointer",
                            backgroundColor: answer.answer ? "#E8F5F3" : "transparent",
                            "&:hover": {
                                backgroundColor: "#F8FAFC"
                            }
                        }}
                        onClick={() => handleAnswerChange(question.id, !answer.answer)}
                    >
                        <Checkbox
                            checked={answer.answer || false}
                            sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }}
                        />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            {question.question}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPES.FILE_UPLOAD:
                const handleFileUpload = async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    // Reset file input so the same file can be selected again
                    e.target.value = '';

                    setUploadingFiles(prev => ({ ...prev, [question.id]: true }));
                    try {
                        // Determine the correct file type based on file extension/mime
                        const isImage = file.type.startsWith('image/');
                        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
                        const isDoc = file.type.includes('document') || file.name.toLowerCase().match(/\.(doc|docx)$/);

                        let cdnUrl;
                        if (isImage) {
                            cdnUrl = await uploadImage(file);
                        } else if (isPdf || isDoc) {
                            cdnUrl = await uploadPdf(file);
                        } else {
                            // Default to DOC for unknown types
                            cdnUrl = await uploadMedia(file, 'DOC');
                        }

                        // Replace the existing file with the new one (overwrite)
                        setAnswers(prev => ({
                            ...prev,
                            [question.id]: {
                                answer: cdnUrl,
                                details: ""
                            }
                        }));
                        notificationHandler.success("הקובץ הועלה בהצלחה");
                    } catch (error) {
                        console.error('Error uploading file:', error);
                        notificationHandler.error("שגיאה בהעלאת הקובץ");
                    } finally {
                        setUploadingFiles(prev => ({ ...prev, [question.id]: false }));
                    }
                };

                return (
                    <Box>
                        <input
                            type="file"
                            ref={(ref) => { fileInputRefs.current[question.id] = ref; }}
                            onChange={handleFileUpload}
                            accept="image/*,.pdf,.doc,.docx"
                            style={{ display: 'none' }}
                        />
                        {answer.answer ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "8px 12px",
                                    backgroundColor: "#F8FAFC",
                                    borderRadius: "8px",
                                    border: "1px solid #E2E8F0"
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <AttachFileIcon sx={{ color: "#57837E", fontSize: 18 }} />
                                    <Typography
                                        component="a"
                                        href={answer.answer}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            fontSize: "13px",
                                            color: "#57837E",
                                            fontFamily: '"Heebo", "Roboto", sans-serif',
                                            textDecoration: "none",
                                            maxWidth: "200px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            "&:hover": { textDecoration: "underline" }
                                        }}
                                    >
                                        צפה / הורד
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => fileInputRefs.current[question.id]?.click()}
                                disabled={uploadingFiles[question.id]}
                                startIcon={uploadingFiles[question.id] ?
                                    <CircularProgress size={14} color="inherit" /> :
                                    <AttachFileIcon sx={{ marginLeft: "4px" }} />
                                }
                                sx={{
                                    borderColor: "#E2E8F0",
                                    color: "#64748B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    "&:hover": {
                                        borderColor: "#57837E",
                                        backgroundColor: "#F8FAFC"
                                    }
                                }}
                            >
                                {uploadingFiles[question.id] ? "מעלה..." : "העלה קובץ"}
                            </Button>
                        )}
                    </Box>
                );

            case QUESTION_TYPES.SIGNATURE:
                const clearSignature = () => {
                    // Clear the canvas if it exists
                    const sigCanvas = signatureRefs.current[question.id];
                    if (sigCanvas) {
                        sigCanvas.clear();
                    }
                    // Always clear the answer to allow new signature
                    handleAnswerChange(question.id, "");
                };

                const saveSignature = async () => {
                    const sigCanvas = signatureRefs.current[question.id];
                    if (!sigCanvas || sigCanvas.isEmpty()) {
                        notificationHandler.error("אנא חתום לפני השמירה");
                        return;
                    }

                    setUploadingFiles(prev => ({ ...prev, [`sig_${question.id}`]: true }));
                    try {
                        // Convert canvas to blob
                        const canvas = sigCanvas.getCanvas();
                        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                        const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });

                        // Upload to S3
                        const cdnUrl = await uploadImage(file);
                        handleAnswerChange(question.id, cdnUrl);
                        notificationHandler.success("החתימה נשמרה בהצלחה");
                    } catch (error) {
                        console.error('Error saving signature:', error);
                        notificationHandler.error("שגיאה בשמירת החתימה");
                    } finally {
                        setUploadingFiles(prev => ({ ...prev, [`sig_${question.id}`]: false }));
                    }
                };

                return (
                    <Box>
                        <Box
                            sx={{
                                border: "2px solid #E2E8F0",
                                borderRadius: "8px",
                                backgroundColor: "#FFFFFF",
                                position: "relative",
                                overflow: "hidden"
                            }}
                        >
                            {answer.answer ? (
                                <Box sx={{ position: "relative" }}>
                                    <img
                                        src={answer.answer}
                                        alt="חתימה"
                                        style={{
                                            width: "100%",
                                            height: "200px",
                                            objectFit: "contain",
                                            backgroundColor: "#FFFFFF"
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={clearSignature}
                                        sx={{
                                            position: "absolute",
                                            top: "8px",
                                            left: "8px",
                                            color: "#EF4444",
                                            borderColor: "#EF4444",
                                            backgroundColor: "#FFFFFF",
                                            fontFamily: '"Heebo", "Roboto", sans-serif',
                                            "&:hover": {
                                                borderColor: "#DC2626",
                                                backgroundColor: "#FEF2F2"
                                            }
                                        }}
                                    >
                                        נקה חתימה
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative" }}>
                                    <SignatureCanvas
                                        ref={(ref) => { signatureRefs.current[question.id] = ref; }}
                                        canvasProps={{
                                            style: {
                                                width: "100%",
                                                height: "200px",
                                                cursor: "crosshair",
                                                touchAction: "none"
                                            }
                                        }}
                                        backgroundColor="#FFFFFF"
                                        penColor="#1E293B"
                                    />
                                    <Typography
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            color: "#CBD5E1",
                                            fontFamily: '"Heebo", "Roboto", sans-serif',
                                            pointerEvents: "none",
                                            fontSize: "14px"
                                        }}
                                    >
                                        חתום כאן
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        {!answer.answer && (
                            <Box sx={{ display: "flex", gap: 1, marginTop: "8px" }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={saveSignature}
                                    disabled={uploadingFiles[`sig_${question.id}`]}
                                    sx={{
                                        backgroundColor: "#57837E",
                                        fontFamily: '"Heebo", "Roboto", sans-serif',
                                        "&:hover": {
                                            backgroundColor: "#46706B"
                                        }
                                    }}
                                >
                                    {uploadingFiles[`sig_${question.id}`] ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        "שמור חתימה"
                                    )}
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={clearSignature}
                                    sx={{
                                        color: "#64748B",
                                        fontFamily: '"Heebo", "Roboto", sans-serif',
                                        "&:hover": {
                                            backgroundColor: "#F8FAFC"
                                        }
                                    }}
                                >
                                    נקה
                                </Button>
                            </Box>
                        )}
                    </Box>
                );

            case QUESTION_TYPES.PDF_ATTACHMENT:
                return (
                    <Box
                        sx={{
                            backgroundColor: "#F8FAFC",
                            border: "1px solid #E2E8F0",
                            borderRadius: "8px",
                            padding: "16px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}
                    >
                        <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            {question.pdfName || "קובץ PDF מצורף"}
                        </Typography>
                        {question.pdfUrl && (
                            <Button
                                variant="text"
                                href={question.pdfUrl}
                                target="_blank"
                                sx={{ color: "#57837E", fontFamily: '"Heebo", "Roboto", sans-serif' }}
                            >
                                צפה / הורד
                            </Button>
                        )}
                    </Box>
                );

            case QUESTION_TYPES.LINK:
                return (
                    <Box
                        sx={{
                            backgroundColor: "#E8F5F3",
                            border: "1px solid #B9D9D5",
                            borderRadius: "8px",
                            padding: "16px"
                        }}
                    >
                        <Typography
                            component="a"
                            href={question.linkUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: "#57837E",
                                fontWeight: 600,
                                textDecoration: "none",
                                fontSize: "15px",
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": { textDecoration: "underline" }
                            }}
                        >
                            {question.question || "קישור"} ↗
                        </Typography>
                        {question.linkDescription && (
                            <Typography sx={{ fontSize: "13px", color: "#64748B", marginTop: "8px" }}>
                                {question.linkDescription}
                            </Typography>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    // Check if question needs a label (not display-only types)
    const needsLabel = (questionType) => {
        return ![
            QUESTION_TYPES.FORM_TITLE,
            QUESTION_TYPES.SECTION_TITLE,
            QUESTION_TYPES.FREE_TEXT,
            QUESTION_TYPES.CHECKBOX,
            QUESTION_TYPES.LINK,
            QUESTION_TYPES.PDF_ATTACHMENT
        ].includes(questionType);
    };

    if (!form) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: coach ? coachT.r : "12px",
                    direction: "rtl",
                    maxHeight: "90vh"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 24px",
                    backgroundColor: coach ? coachT.bg : undefined,
                    borderBottom: coach ? `1px solid ${coachT.border}` : "1px solid #E2E8F0"
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                        <BackIcon />
                    </IconButton>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "18px",
                                fontWeight: coach ? 800 : 600,
                                color: coach ? coachT.text : "#1E293B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מילוי טופס - {form.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            מתאמן: {clientName}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent sx={{ padding: "24px" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "48px" }}>
                        <CircularProgress sx={{ color: "#57837E" }} />
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: "700px", margin: "0 auto" }}>
                        {/* Info banner */}
                        <Paper
                            sx={{
                                padding: "12px 16px",
                                backgroundColor: "#FEF3C7",
                                borderRadius: "8px",
                                marginBottom: "24px",
                                border: "1px solid #FCD34D"
                            }}
                            elevation={0}
                        >
                            <Typography sx={{
                                fontSize: "14px",
                                color: "#92400E",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}>
                                מילוי התשובות בשם המתאמן. השינויים יישמרו רק לאחר לחיצה על "שמור תשובות".
                            </Typography>
                        </Paper>

                        {/* Questions */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                            {questions.map((question, index) => {
                                // Hide FILE_UPLOAD question if file already exists
                                if (question.questionType === QUESTION_TYPES.FILE_UPLOAD && answers[question.id]?.answer) {
                                    return null;
                                }
                                return (
                                <Paper
                                    key={question.id}
                                    sx={{
                                        padding: "20px",
                                        backgroundColor: "#FFFFFF",
                                        borderRadius: "8px",
                                        border: "1px solid #E2E8F0"
                                    }}
                                    elevation={0}
                                >
                                    {needsLabel(question.questionType) && (
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: "15px",
                                                color: "#1E293B",
                                                marginBottom: "12px",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}
                                        >
                                            {question.question}
                                            {question.required && (
                                                <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>
                                            )}
                                        </Typography>
                                    )}
                                    {renderQuestionInput(question)}
                                    {/* Container-level validation error (e.g. "שדה חובה" for a required
                                        field). EMAIL/PHONE surface their own error inline via helperText,
                                        so skip them here to avoid a duplicate message. */}
                                    {validationErrors[question.id]
                                        && question.questionType !== QUESTION_TYPES.EMAIL
                                        && question.questionType !== QUESTION_TYPES.PHONE && (
                                        <Typography
                                            sx={{
                                                color: "#EF4444",
                                                fontSize: "13px",
                                                marginTop: "8px",
                                                fontFamily: '"Heebo", "Roboto", sans-serif'
                                            }}
                                        >
                                            {validationErrors[question.id]}
                                        </Typography>
                                    )}
                                </Paper>
                                );
                            })}
                        </Box>

                        {/* Submit button at bottom */}
                        <Box sx={{ marginTop: "32px", display: "flex", justifyContent: "center" }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ marginLeft: "8px" }} />}
                                sx={{
                                    backgroundColor: coach ? coachT.green : "#57837E",
                                    borderRadius: coach ? coachT.rs : undefined,
                                    boxShadow: coach ? "none" : undefined,
                                    padding: "12px 48px",
                                    fontSize: "16px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    fontWeight: 600,
                                    "&:hover": {
                                        backgroundColor: coach ? coachT.greenDark : "#46706B",
                                        boxShadow: coach ? "none" : undefined
                                    },
                                    "&:disabled": {
                                        backgroundColor: "#94A3B8"
                                    }
                                }}
                            >
                                {saving ? "שומר..." : "שלח טופס"}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FormEditDialog;
